import Polyfill from "../Polyfill";
import {CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";

interface SelectedImage {
    folder: string;
    file: string;
}

/**
 * The selection of images on an event page.
 *
 * In buy mode every cartable thumbnail carries a tick control. Ticked
 * images are collected here and counted in the active panel of the buy
 * bar, from where the format dialog for the whole selection is opened.
 * The bar itself, and whether it shows, is handled by the buy mode code.
 *
 * The selection is kept in the session storage under the path of the page,
 * so it survives ajax paging, server side paging and a reload of the same
 * event. Once the images went into the cart the selection is cleared.
 */
export class SelectionManager {
    static TOGGLE_SELECTOR = '.eventgallery-select-toggle';
    static BAR_SELECTOR = '.eventgallery-selection-bar';
    static THUMB_SELECTOR = '.thumbnail-container, .ajax-thumbnail-container';
    static SELECTED_CLASS = 'is-selected';
    static STORAGE_PREFIX = 'eventgallery.selection.';

    private cartProcessor: CartProcessor;
    private selected: Map<string, SelectedImage> = new Map();
    private bar: HTMLElement | null;
    private countElement: HTMLElement | null;
    private chooseButton: HTMLButtonElement | null;
    private bound: WeakSet<Element> = new WeakSet();

    constructor(cartProcessor: CartProcessor, container: HTMLElement) {
        this.cartProcessor = cartProcessor;
        this.bar = container.querySelector(SelectionManager.BAR_SELECTOR);
        this.countElement = this.bar ? this.bar.querySelector('.selection-count') : null;
        this.chooseButton = this.bar ? this.bar.querySelector('.selection-choose-format') : null;

        this.restore();
        this.bind();

        if (this.bar) {
            this.bar.querySelector('.selection-select-all')?.addEventListener('click', (e) => {
                e.preventDefault();
                this.selectAll();
            });
            this.bar.querySelector('.selection-clear')?.addEventListener('click', (e) => {
                e.preventDefault();
                this.clear();
            });
            this.chooseButton?.addEventListener('click', (e) => {
                e.preventDefault();
                this.openDialog();
            });
        }

        document.addEventListener(Events.rebindAdd2CartButtons, () => this.bind());
        document.addEventListener('eventgallery-images-added', () => this.bind());
        document.addEventListener(Events.selectionAdded, () => this.clear());

        this.render();
    }

    /**
     * Wires every tick control on the page. New thumbnails arrive with
     * ajax paging, so this runs again whenever the page announces them.
     */
    bind() {
        const toggles = document.querySelectorAll<HTMLElement>(SelectionManager.TOGGLE_SELECTOR);
        for (let i = 0; i < toggles.length; i++) {
            const toggle = toggles[i];
            if (!this.bound.has(toggle)) {
                this.bound.add(toggle);
                toggle.addEventListener('click', (e) => {
                    // the control sits inside the thumbnail link; the click must not open the lightbox
                    e.preventDefault();
                    e.stopPropagation();
                    this.toggle(toggle);
                });
                toggle.addEventListener('keydown', (e: KeyboardEvent) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        this.toggle(toggle);
                    }
                });
            }
            this.applyState(toggle);
        }
    }

    toggle(toggle: HTMLElement) {
        const image = this.imageOf(toggle);
        if (!image) {
            return;
        }
        const id = this.idFor(image);
        if (this.selected.has(id)) {
            this.selected.delete(id);
        } else {
            this.selected.set(id, image);
        }
        this.applyState(toggle);
        this.persist();
        this.render();
        this.dispatchChange();
    }

    selectAll() {
        const toggles = document.querySelectorAll<HTMLElement>(SelectionManager.TOGGLE_SELECTOR);
        for (let i = 0; i < toggles.length; i++) {
            const image = this.imageOf(toggles[i]);
            if (image) {
                this.selected.set(this.idFor(image), image);
            }
            this.applyState(toggles[i]);
        }
        this.persist();
        this.render();
        this.dispatchChange();
    }

    clear() {
        this.selected.clear();
        const toggles = document.querySelectorAll<HTMLElement>(SelectionManager.TOGGLE_SELECTOR);
        for (let i = 0; i < toggles.length; i++) {
            this.applyState(toggles[i]);
        }
        this.persist();
        this.render();
        this.dispatchChange();
    }

    getSelected(): SelectedImage[] {
        return Array.from(this.selected.values());
    }

    openDialog() {
        const items = this.getSelected();
        if (items.length === 0) {
            return;
        }
        // @ts-ignore
        const cart = window.Eventgallery?.cart;
        if (cart && typeof cart.openMultiDialog === 'function') {
            cart.openMultiDialog(items);
        }
    }

    /**
     * The image a tick control stands for, read from its data attributes.
     */
    private imageOf(toggle: HTMLElement): SelectedImage | null {
        const folder = toggle.getAttribute('data-folder') || '';
        const file = toggle.getAttribute('data-file') || '';
        return folder && file ? {folder: folder, file: file} : null;
    }

    private applyState(toggle: HTMLElement) {
        const image = this.imageOf(toggle);
        const isSelected = image !== null && this.selected.has(this.idFor(image));
        toggle.classList.toggle(SelectionManager.SELECTED_CLASS, isSelected);
        toggle.setAttribute('aria-checked', isSelected ? 'true' : 'false');

        const thumb = toggle.closest(SelectionManager.THUMB_SELECTOR);
        if (thumb) {
            thumb.classList.toggle(SelectionManager.SELECTED_CLASS, isSelected);
        }
    }

    private render() {
        if (!this.bar) {
            return;
        }
        const count = this.selected.size;

        if (this.countElement) {
            const template = count > 0
                ? (this.bar.getAttribute('data-count-template') || '{count}')
                : (this.bar.getAttribute('data-count-none') || '');
            this.countElement.textContent = template.replace('{count}', String(count));
        }
        if (this.chooseButton) {
            this.chooseButton.disabled = count === 0;
        }
    }

    private storageKey(): string {
        return SelectionManager.STORAGE_PREFIX + window.location.pathname;
    }

    private persist() {
        try {
            if (this.selected.size === 0) {
                window.sessionStorage.removeItem(this.storageKey());
            } else {
                window.sessionStorage.setItem(this.storageKey(), JSON.stringify(this.getSelected()));
            }
        } catch (e) {
            // storage may be blocked; the selection then lives as long as the page
        }
    }

    private restore() {
        try {
            const stored = window.sessionStorage.getItem(this.storageKey());
            if (!stored) {
                return;
            }
            const items: SelectedImage[] = JSON.parse(stored);
            items.forEach((item) => {
                if (item && item.folder && item.file) {
                    this.selected.set(this.idFor(item), item);
                }
            });
        } catch (e) {
            this.selected.clear();
        }
    }

    /**
     * Key of an image in the selection. Built from the raw names, so it does
     * not depend on how the server encoded the data-id attribute.
     */
    private idFor(item: SelectedImage): string {
        return item.folder + '|' + item.file;
    }

    /**
     * Tells the page that the selection changed; nothing listens yet, but a
     * template override may want to.
     */
    private dispatchChange() {
        document.dispatchEvent(Polyfill.createNewEvent('eventgallery-selection-changed', {count: this.selected.size}));
    }
}
